import { useCallback, useState } from 'react'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import useSentry from 'Pods/Shared/Hooks/useSentry'

const SOLIDGATE_REQUEST_FAILED = 'solidgate_request_failed'

export const RETRY_STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUBMITTED: 'submitted',
}

// The "other" option carries free-typed user text; only the reason bucket may reach
// Sentry (PII — keep telemetry clean of user-entered content).
const reasonBucket = (reason) => (
  typeof reason === 'string' ? reason.replace(/other:[\s\S]*$/, 'other') : reason
)

function useSubscription({
  subscriptionData,
  messages,
  onCancelSuccess = null,
  onRestoreSuccess,
  onRestoreError,
  onRedirectToPaymentForm,
  onCancelPlanSwitchSuccess,
  onPauseError,
  onResumeError,
}) {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [retryState, setRetryState] = useState(RETRY_STATE.IDLE)
  const [isPauseLoading, setIsPauseLoading] = useState(false)
  const [isResumeLoading, setIsResumeLoading] = useState(false)
  const { postData, isLoading } = useApiPost()
  // Separate instance: the survey capture is fire-and-forget and must not toggle the
  // shared isLoading consumed as the restore/cancel loading state.
  const { postData: postReasonData } = useApiPost()
  const { deleteData, isLoading: isCancelPlanSwitchLoading } = useApiDelete()
  const { showSuccess, showError } = useNotifications()
  const { captureException } = useSentry()

  const handleOpenCancelModal = useCallback(() => {
    setIsCancelModalOpen(true)
  }, [])

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false)
  }, [])

  // Resolves to true only once the cancellation response has landed, so callers can gate
  // success tracking and the confirmation step on the real outcome. Returning a bare
  // undefined here made `await onSubmit(...)` in useCancelModal resolve on the next
  // microtask: every attempt tracked as confirmed and closed the modal before the request
  // finished, including the ones that failed.
  const handleCancelSubscription = useCallback(
    (reason) => {
      if (!subscriptionData?.id) return Promise.resolve(false)

      return new Promise((resolve) => {
        postData({
          path: '/api/subscriptions/cancel_subscription',
          data: { id: subscriptionData.id, reason },
          onSuccess: () => {
            resolve(true)

            // v1.6 shows a confirmation modal before refreshing; flag-off keeps the
            // straight reload.
            if (onCancelSuccess) {
              onCancelSuccess()
              return
            }

            window.location.reload()
          },
          onError: (error) => {
            const errorMessage = error.response?.data?.error === SOLIDGATE_REQUEST_FAILED
              ? messages.pauseError(SOLIDGATE_REQUEST_FAILED)
              : error.response?.data?.error_message || messages.cancelError
            showError(errorMessage)
            captureException(error, {
              tags: { context: 'cancel_subscription' },
              extra: { subscriptionId: subscriptionData.id, reason: reasonBucket(reason) },
            })
            resolve(false)
          },
        })
      })
    },
    [subscriptionData, messages, onCancelSuccess, postData, showError, captureException]
  )

  // Persists the survey answer as the user advances past the reason step, before the
  // final confirm — best-effort: failures are reported to Sentry but never block the flow.
  const handleCaptureCancellationReason = useCallback(
    (reason) => {
      if (!subscriptionData?.id) return

      postReasonData({
        path: '/api/subscriptions/cancellation_reason',
        data: { id: subscriptionData.id, reason },
        onError: (error) => {
          captureException(error, {
            tags: { context: 'cancellation_reason' },
            extra: { subscriptionId: subscriptionData.id, reason: reasonBucket(reason) },
          })
        },
      })
    },
    [subscriptionData, postReasonData, captureException]
  )

  const handleRestoreSubscription = useCallback(() => {
    if (!subscriptionData?.id) return

    postData({
      path: '/api/subscriptions/restore_subscription',
      data: { id: subscriptionData.id },
      onSuccess: (response) => {
        if (response?.data?.redirectTo) {
          onRedirectToPaymentForm?.(response.data.redirectTo)
          return
        }

        const renewalScheduled = Boolean(response?.data?.renewal_scheduled)

        showSuccess(messages.restoreSuccess)
        onRestoreSuccess?.({ renewalScheduled })
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error_message || messages.restoreError
        showError(errorMessage)
        captureException(error, {
          tags: { context: 'restore_subscription' },
          extra: { subscriptionId: subscriptionData.id },
        })
        onRestoreError?.(errorMessage)
      },
    })
  }, [
    subscriptionData,
    messages,
    onRestoreSuccess,
    onRestoreError,
    onRedirectToPaymentForm,
    postData,
    showSuccess,
    showError,
    captureException,
  ])

  const handleRetryPayment = useCallback(() => {
    if (!subscriptionData?.id) return

    setRetryState(RETRY_STATE.LOADING)

    postData({
      path: '/profile/subscription/retry-payment',
      onSuccess: () => {
        setRetryState(RETRY_STATE.SUBMITTED)
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error_message || messages.retryError
        showError(errorMessage)
        setRetryState(RETRY_STATE.IDLE)
        captureException(error, {
          tags: { context: 'retry_payment' },
          extra: { subscriptionId: subscriptionData.id },
        })
      },
    })
  }, [subscriptionData, messages, postData, showError, captureException])

  // Resolves to true on success so callers can chain UI steps (confirmation modal, reload).
  // The backend never returns pause dates here — Solidgate webhooks are authoritative, so
  // callers must refetch instead of updating lifecycle state locally.
  const handlePauseSubscription = useCallback(() => {
    if (!subscriptionData?.id || isPauseLoading) return Promise.resolve(false)

    setIsPauseLoading(true)

    return new Promise((resolve) => {
      postData({
        path: '/api/subscriptions/pause_subscription',
        data: { id: subscriptionData.id },
        onSuccess: () => {
          setIsPauseLoading(false)
          resolve(true)
        },
        onError: (error) => {
          const errorCode = error.response?.data?.error
          setIsPauseLoading(false)
          showError(messages.pauseError(errorCode))
          captureException(error, {
            tags: { context: 'pause_subscription' },
            extra: { subscriptionId: subscriptionData.id, errorCode },
          })
          onPauseError?.(errorCode)
          resolve(false)
        },
      })
    })
  }, [subscriptionData, isPauseLoading, messages, postData, showError, captureException, onPauseError])

  const handleResumeSubscription = useCallback(() => {
    if (!subscriptionData?.id || isResumeLoading) return Promise.resolve(false)

    setIsResumeLoading(true)

    return new Promise((resolve) => {
      postData({
        path: '/api/subscriptions/resume_subscription',
        data: { id: subscriptionData.id },
        onSuccess: () => {
          setIsResumeLoading(false)
          resolve(true)
        },
        onError: (error) => {
          const errorCode = error.response?.data?.error
          setIsResumeLoading(false)
          showError(messages.resumeError(errorCode))
          captureException(error, {
            tags: { context: 'resume_subscription' },
            extra: { subscriptionId: subscriptionData.id, errorCode },
          })
          onResumeError?.(errorCode)
          resolve(false)
        },
      })
    })
  }, [subscriptionData, isResumeLoading, messages, postData, showError, captureException, onResumeError])

  const handleCancelPlanSwitch = useCallback(() => {
    deleteData({
      path: '/profile/subscription/plan_switch',
      onSuccess: () => {
        showSuccess(messages.cancelPlanSwitchSuccess)
        onCancelPlanSwitchSuccess?.()
      },
      onError: (error) => {
        showError(error.response?.data?.error || messages.cancelPlanSwitchError)
        captureException(error, { tags: { context: 'cancel_plan_switch' } })
      },
    })
  }, [deleteData, messages, showSuccess, showError, captureException, onCancelPlanSwitchSuccess])

  return {
    isCancelModalOpen,
    isLoading,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    // Both call restore_subscription — backend determines action based on subscription state
    handleRenewSubscription: handleRestoreSubscription,
    handleReactivateSubscription: handleRestoreSubscription,
    handleRetryPayment,
    retryState,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    handlePauseSubscription,
    isPauseLoading,
    handleResumeSubscription,
    isResumeLoading,
  }
}

export default useSubscription
